import { useState } from "react";
import { ChevronUp, ChevronDown, Pencil, Trash2, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExperienceSection } from "@/types/experience-section";

type ExperienceSectionEditorProps = {
  section: ExperienceSection;
  children: React.ReactNode;
  onEdit: (section: ExperienceSection) => void;
  onDelete: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  isAdmin: boolean;
  isFirst: boolean;
  isLast: boolean;
  orderNumber: number;
};

const ExperienceSectionEditor = ({
  section,
  children,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  isAdmin,
  isFirst,
  isLast,
  orderNumber,
}: ExperienceSectionEditorProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (!isAdmin) {
    return <>{children}</>;
  }

  return (
    <>
      <div className="relative group">
        {/* Admin controls floating on the left - order controls */}
        <div 
          className="absolute top-0 flex flex-col items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-card/90 backdrop-blur-sm rounded-lg border shadow-sm p-1"
          style={{ left: '-56px' }}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onMoveUp(section.id)}
            disabled={isFirst}
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <span className="text-xs font-medium text-muted-foreground w-6 text-center">
            {orderNumber}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onMoveDown(section.id)}
            disabled={isLast}
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>

        {/* Admin controls floating on the right - edit/delete */}
        <div 
          className="absolute top-0 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-card/90 backdrop-blur-sm rounded-lg border shadow-sm p-1"
          style={{ right: '-44px' }}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onEdit(section)}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>

        {/* Active/Inactive indicator */}
        {!section.is_active && (
          <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full flex items-center gap-1 border shadow-sm z-10">
            <EyeOff className="h-3 w-3" />
            Hidden
          </div>
        )}

        {/* Section content - unchanged layout */}
        <div className="group-hover:ring-2 group-hover:ring-primary/20 group-hover:ring-offset-2 rounded-lg transition-all">
          {children}
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Section</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{section.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(section.id);
                setShowDeleteDialog(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExperienceSectionEditor;
